import React, { useState, useEffect } from 'react';
import { 
  DollarSign,
  Calendar,
  Users,
  Plus,
  Search,
  Filter,
  Download,
  Eye,
  CheckCircle,
  Clock,
  AlertCircle,
  FileText,
  CreditCard,
  TrendingUp,
  Edit3,
  Trash2,
  X
} from 'lucide-react';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  orderBy,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
  deleteDoc
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useRealtime } from '../../contexts/RealtimeContext';
import toast from 'react-hot-toast';

const TeacherSettlementManagement = ({ coachingId }) => {
  const { currentUser, userProfile } = useRealtime();
  const [settlements, setSettlements] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterTeacher, setFilterTeacher] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedSettlement, setSelectedSettlement] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [stats, setStats] = useState({
    totalSettlements: 0,
    pendingAmount: 0,
    paidAmount: 0,
    thisMonthSettlements: 0
  });

  const [newSettlement, setNewSettlement] = useState({
    teacherId: '',
    period: '',
    amount: 0,
    description: '',
    details: {
      classesTaken: 0,
      ratePerClass: 0,
      bonus: 0,
      deductions: 0
    },
    dueDate: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    if (coachingId) {
      loadSettlements();
      loadTeachers();
    }
  }, [coachingId]);

  const loadSettlements = async () => {
    try {
      setLoading(true);
      const settlementsQuery = query(
        collection(db, 'teacherSettlements'),
        where('coachingId', '==', coachingId),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(settlementsQuery);
      const settlementsList = [];
      
      snapshot.forEach((doc) => {
        settlementsList.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      setSettlements(settlementsList);
      calculateStats(settlementsList);
    } catch (error) {
      console.error('Error loading settlements:', error);
      toast.error('Failed to load settlements');
    } finally {
      setLoading(false);
    }
  };

  const loadTeachers = async () => {
    try {
      const teachersQuery = query(
        collection(db, 'users'),
        where('role', '==', 'teacher'),
        where('coachingId', '==', coachingId)
      );
      
      const snapshot = await getDocs(teachersQuery);
      const teachersList = [];
      
      snapshot.forEach((doc) => {
        teachersList.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      setTeachers(teachersList);
    } catch (error) {
      console.error('Error loading teachers:', error);
    }
  };

  const calculateStats = (settlementsList) => {
    const totalSettlements = settlementsList.length;
    const pendingAmount = settlementsList
      .filter(s => s.status === 'pending')
      .reduce((sum, settlement) => sum + (settlement.amount || 0), 0);
    const paidAmount = settlementsList
      .filter(s => s.status === 'paid')
      .reduce((sum, settlement) => sum + (settlement.amount || 0), 0);
    
    // Calculate this month's settlements
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    const thisMonthSettlements = settlementsList
      .filter(s => {
        const settlementDate = s.createdAt?.toDate ? s.createdAt.toDate() : new Date(s.createdAt);
        return settlementDate.getMonth() === currentMonth && settlementDate.getFullYear() === currentYear;
      }).length;

    setStats({
      totalSettlements,
      pendingAmount,
      paidAmount,
      thisMonthSettlements
    });
  };

  const handleCreateSettlement = async () => {
    if (!newSettlement.teacherId || !newSettlement.amount || newSettlement.amount <= 0) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      const teacher = teachers.find(t => t.id === newSettlement.teacherId);
      if (!teacher) {
        toast.error('Selected teacher not found');
        return;
      }

      const settlementData = {
        ...newSettlement,
        teacherName: teacher.displayName || teacher.email,
        teacherEmail: teacher.email,
        coachingId,
        status: 'pending',
        createdAt: serverTimestamp(),
        createdBy: currentUser?.uid
      };

      await addDoc(collection(db, 'teacherSettlements'), settlementData);
      
      toast.success('Settlement created successfully');
      setShowCreateModal(false);
      setNewSettlement({
        teacherId: '',
        period: '',
        amount: 0,
        description: '',
        details: {
          classesTaken: 0,
          ratePerClass: 0,
          bonus: 0,
          deductions: 0
        },
        dueDate: new Date().toISOString().split('T')[0]
      });
      loadSettlements();
    } catch (error) {
      console.error('Error creating settlement:', error);
      toast.error('Failed to create settlement');
    }
  };

  const handleUpdateSettlementStatus = async (settlementId, newStatus, paymentDetails = null) => {
    try {
      const updateData = {
        status: newStatus,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.uid
      };

      if (newStatus === 'paid' && paymentDetails) {
        updateData.paymentDetails = {
          ...paymentDetails,
          paidAt: serverTimestamp()
        };
      }

      await updateDoc(doc(db, 'teacherSettlements', settlementId), updateData);
      
      toast.success(`Settlement marked as ${newStatus}`);
      loadSettlements();
    } catch (error) {
      console.error('Error updating settlement:', error);
      toast.error('Failed to update settlement');
    }
  };

  const handleDeleteSettlement = async (settlementId) => {
    if (!window.confirm('Are you sure you want to delete this settlement?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'teacherSettlements', settlementId));
      toast.success('Settlement deleted successfully');
      loadSettlements();
    } catch (error) {
      console.error('Error deleting settlement:', error);
      toast.error('Failed to delete settlement');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'processing':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircle className="w-4 h-4" />;
      case 'pending':
        return <Clock className="w-4 h-4" />;
      case 'processing':
        return <CreditCard className="w-4 h-4" />;
      case 'rejected':
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const filteredSettlements = settlements.filter(settlement => {
    const matchesSearch = settlement.teacherName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         settlement.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         settlement.period?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || settlement.status === filterStatus;
    const matchesTeacher = filterTeacher === 'all' || settlement.teacherId === filterTeacher;
    
    return matchesSearch && matchesStatus && matchesTeacher;
  });

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const settlementDate = date.toDate ? date.toDate() : new Date(date);
    return settlementDate.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
            <DollarSign className="w-6 h-6 mr-2 text-green-600" />
            Teacher Settlements
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Manage teacher payments and settlement history
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
        >
          <Plus className="w-5 h-5" />
          <span>Create Settlement</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Settlements</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats.totalSettlements}
              </p>
            </div>
            <FileText className="w-8 h-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Pending Amount</p>
              <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                {formatCurrency(stats.pendingAmount)}
              </p>
            </div>
            <Clock className="w-8 h-8 text-yellow-500" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Paid Amount</p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                {formatCurrency(stats.paidAmount)}
              </p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">This Month</p>
              <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {stats.thisMonthSettlements}
              </p>
            </div>
            <Calendar className="w-8 h-8 text-purple-500" />
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search settlements..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>
          <div className="flex items-center space-x-2">
            <Filter className="w-5 h-5 text-gray-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="paid">Paid</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div className="flex items-center space-x-2">
            <Users className="w-5 h-5 text-gray-400" />
            <select
              value={filterTeacher}
              onChange={(e) => setFilterTeacher(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value="all">All Teachers</option>
              {teachers.map((teacher) => (
                <option key={teacher.id} value={teacher.id}>
                  {teacher.displayName || teacher.email}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Settlements List */}
        {filteredSettlements.length === 0 ? (
          <div className="text-center py-12">
            <DollarSign className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No Settlements Found
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {settlements.length === 0 
                ? "Create your first teacher settlement"
                : "No settlements match your search criteria"
              }
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredSettlements.map((settlement) => (
              <div key={settlement.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {settlement.teacherName}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {settlement.description || `Settlement for ${settlement.period}`}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Created: {formatDate(settlement.createdAt)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {formatCurrency(settlement.amount)}
                    </p>
                    <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(settlement.status)}`}>
                      {getStatusIcon(settlement.status)}
                      <span className="capitalize">{settlement.status}</span>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setSelectedSettlement(settlement);
                      setShowDetailsModal(true);
                    }}
                    className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm transition-colors"
                  >
                    <Eye className="w-4 h-4 inline mr-1" />
                    View Details
                  </button>
                  
                  {settlement.status === 'pending' && (
                    <button
                      onClick={() => handleUpdateSettlementStatus(settlement.id, 'paid', {
                        method: 'bank_transfer',
                        transactionId: `TXN${Date.now()}`
                      })}
                      className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm transition-colors"
                    >
                      <CheckCircle className="w-4 h-4 inline mr-1" />
                      Mark Paid
                    </button>
                  )}
                  
                  <button
                    onClick={() => handleDeleteSettlement(settlement.id)}
                    className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm transition-colors"
                  >
                    <Trash2 className="w-4 h-4 inline mr-1" />
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Settlement Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">Create New Settlement</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Teacher *
                  </label>
                  <select
                    value={newSettlement.teacherId}
                    onChange={(e) => setNewSettlement(prev => ({ ...prev, teacherId: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Select Teacher</option>
                    {teachers.map((teacher) => (
                      <option key={teacher.id} value={teacher.id}>
                        {teacher.displayName || teacher.email}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Period *
                  </label>
                  <input
                    type="text"
                    value={newSettlement.period}
                    onChange={(e) => setNewSettlement(prev => ({ ...prev, period: e.target.value }))}
                    placeholder="e.g., January 2024"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Amount *
                  </label>
                  <input
                    type="number"
                    value={newSettlement.amount}
                    onChange={(e) => setNewSettlement(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
                    placeholder="Enter amount"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Due Date
                  </label>
                  <input
                    type="date"
                    value={newSettlement.dueDate}
                    onChange={(e) => setNewSettlement(prev => ({ ...prev, dueDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={newSettlement.description}
                  onChange={(e) => setNewSettlement(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Settlement description..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Classes Taken
                  </label>
                  <input
                    type="number"
                    value={newSettlement.details.classesTaken}
                    onChange={(e) => setNewSettlement(prev => ({ 
                      ...prev, 
                      details: { ...prev.details, classesTaken: parseInt(e.target.value) || 0 }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Rate per Class
                  </label>
                  <input
                    type="number"
                    value={newSettlement.details.ratePerClass}
                    onChange={(e) => setNewSettlement(prev => ({ 
                      ...prev, 
                      details: { ...prev.details, ratePerClass: parseFloat(e.target.value) || 0 }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Bonus
                  </label>
                  <input
                    type="number"
                    value={newSettlement.details.bonus}
                    onChange={(e) => setNewSettlement(prev => ({ 
                      ...prev, 
                      details: { ...prev.details, bonus: parseFloat(e.target.value) || 0 }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Deductions
                  </label>
                  <input
                    type="number"
                    value={newSettlement.details.deductions}
                    onChange={(e) => setNewSettlement(prev => ({ 
                      ...prev, 
                      details: { ...prev.details, deductions: parseFloat(e.target.value) || 0 }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateSettlement}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg"
              >
                Create Settlement
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settlement Details Modal */}
      {showDetailsModal && selectedSettlement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">Settlement Details</h3>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Basic Information</h4>
                  <div className="space-y-2">
                    <div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">Teacher:</span>
                      <span className="ml-2 font-medium">{selectedSettlement.teacherName}</span>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">Period:</span>
                      <span className="ml-2 font-medium">{selectedSettlement.period}</span>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">Amount:</span>
                      <span className="ml-2 font-medium text-green-600">{formatCurrency(selectedSettlement.amount)}</span>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">Status:</span>
                      <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedSettlement.status)}`}>
                        {selectedSettlement.status}
                      </span>
                    </div>
                  </div>
                </div>

                {selectedSettlement.details && (
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Settlement Breakdown</h4>
                    <div className="space-y-2">
                      <div>
                        <span className="text-sm text-gray-600 dark:text-gray-400">Classes Taken:</span>
                        <span className="ml-2 font-medium">{selectedSettlement.details.classesTaken || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600 dark:text-gray-400">Rate per Class:</span>
                        <span className="ml-2 font-medium">{selectedSettlement.details.ratePerClass ? formatCurrency(selectedSettlement.details.ratePerClass) : 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600 dark:text-gray-400">Bonus:</span>
                        <span className="ml-2 font-medium text-green-600">{formatCurrency(selectedSettlement.details.bonus || 0)}</span>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600 dark:text-gray-400">Deductions:</span>
                        <span className="ml-2 font-medium text-red-600">-{formatCurrency(selectedSettlement.details.deductions || 0)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {selectedSettlement.description && (
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Description</h4>
                  <p className="text-gray-600 dark:text-gray-400">{selectedSettlement.description}</p>
                </div>
              )}

              {selectedSettlement.paymentDetails && selectedSettlement.status === 'paid' && (
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                  <h4 className="font-medium text-green-900 dark:text-green-100 mb-2">Payment Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-green-700 dark:text-green-300">Payment Method</p>
                      <p className="font-medium text-green-900 dark:text-green-100">
                        {selectedSettlement.paymentDetails.method || 'Bank Transfer'}
                      </p>
                    </div>
                    <div>
                      <p className="text-green-700 dark:text-green-300">Transaction ID</p>
                      <p className="font-medium text-green-900 dark:text-green-100">
                        {selectedSettlement.paymentDetails.transactionId || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-green-700 dark:text-green-300">Paid On</p>
                      <p className="font-medium text-green-900 dark:text-green-100">
                        {formatDate(selectedSettlement.paymentDetails.paidAt)}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowDetailsModal(false)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Close
              </button>
              {selectedSettlement.status === 'paid' && (
                <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
                  <Download className="w-4 h-4 inline mr-1" />
                  Download Receipt
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherSettlementManagement;